import "./universeStaffLogin.css";

import { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { serverHost } from "../../../../http_common.js";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardCapslockRoundedIcon from "@mui/icons-material/KeyboardCapslockRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";

import UniLogo from "../../home-page/logo/universe.webp";

import * as StaffAction from "../../../../store/redux/universeStaffReducer.js";

const REMEMBER_KEY = "universe_staff_remembered_id";
const THEME_KEY = "universe_staff_theme";

const ROUTES = {
    CEO: "/executive_officer/workspaces",
    FDC: "/administration/workspace/fdc",
    SDD: "/administration/sdd-workspace",
    ED: "/administration/executive/director-teritory",
};

// Workspace preview content - edit the copy freely
const ROLES = [
    {
        code: "CEO",
        title: "Chief Executive Officer",
        text: "Access executive leadership and company-wide decisions.",
        bars: [68, 78, 73, 86, 82, 93, 88, 97],
    },
    {
        code: "FDC",
        title: "Field Development Consultant",
        text: "Manage your field activity and assigned operations.",
        bars: [42, 58, 36, 70, 52, 80, 64, 90],
    },
    {
        code: "SDD",
        title: "State Development Director",
        text: "Oversee your state's teams and track their performance.",
        bars: [60, 48, 74, 56, 84, 66, 92, 78],
    },
    {
        code: "ED",
        title: "Executive Director",
        text: "See organization-wide performance in a single view.",
        bars: [50, 66, 58, 82, 72, 88, 76, 96],
    },
];

const safeGet = (key) => {
    try {
        return localStorage.getItem(key) || "";
    } catch {
        return "";
    }
};

const safeSet = (key, value) => {
    try {
        if (value) localStorage.setItem(key, value);
        else localStorage.removeItem(key);
    } catch {
        /* storage unavailable (private mode etc.) - safe to ignore */
    }
};

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
};

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function UniverseStaffLogin() {
    const redirect = useNavigate();
    const dispatch = useDispatch();

    const userRef = useRef(null);
    const passRef = useRef(null);

    // idle | loading | success
    const [status, setStatus] = useState("idle");
    const [showPassword, setShowPassword] = useState(false);
    const [capsLockOn, setCapsLockOn] = useState(false);
    const [rememberId, setRememberId] = useState(() =>
        Boolean(safeGet(REMEMBER_KEY)),
    );
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [challenge,setChallenge]=useState(null),[otp,setOtp]=useState("");
    const [errorTick, setErrorTick] = useState(0);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [online, setOnline] = useState(() =>
        typeof navigator === "undefined" ? true : navigator.onLine,
    );

    const [theme, setTheme] = useState(() => {
        const saved = safeGet(THEME_KEY);
        if (saved === "light" || saved === "dark") return saved;
        return window.matchMedia?.("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light";
    });

    const [activeRole, setActiveRole] = useState(1);
    const [autoplay, setAutoplay] = useState(true);
    const [previewHovered, setPreviewHovered] = useState(false);

    const [staffLoginData, setStaffLoginData] = useReducer(
        (request, response) => ({ ...request, ...response }),
        undefined,
        () => ({ user_name: safeGet(REMEMBER_KEY), stud_password: "" }),
    );

    const isBusy = status !== "idle";
    const isEmail = staffLoginData.user_name.includes("@");
    const greeting = getGreeting();

    // Put the cursor where the person needs it
    useEffect(() => {
        (userRef.current?.value ? passRef : userRef).current?.focus();
    }, []);

    // Connection awareness
    useEffect(() => {
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);

        window.addEventListener("online", goOnline);
        window.addEventListener("offline", goOffline);

        return () => {
            window.removeEventListener("online", goOnline);
            window.removeEventListener("offline", goOffline);
        };
    }, []);

    // Workspace preview rotates until someone interacts with it
    useEffect(() => {
        if (!autoplay || previewHovered || prefersReducedMotion())
            return undefined;

        const timer = setInterval(
            () => setActiveRole((index) => (index + 1) % ROLES.length),
            4500,
        );

        return () => clearInterval(timer);
    }, [autoplay, previewHovered]);

    const toggleTheme = () => {
        const next = theme === "dark" ? "light" : "dark";
        setTheme(next);
        safeSet(THEME_KEY, next);
    };

    const pickRole = (index) => {
        setActiveRole(index);
        setAutoplay(false);
    };

    const handleTabKeys = (event) => {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;

        event.preventDefault();

        const step = event.key === "ArrowRight" ? 1 : -1;
        const next = (activeRole + step + ROLES.length) % ROLES.length;

        pickRole(next);
        document.getElementById(`usl-tab-${ROLES[next].code}`)?.focus();
    };

    const handleChange = (field) => (event) => {
        setStaffLoginData({ [field]: event.target.value });
        if (fieldErrors[field])
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        if (formError) setFormError("");
    };

    const handlePasswordKey = (event) => {
        setCapsLockOn(Boolean(event.getModifierState?.("CapsLock")));
    };

    const showError = (message) => {
        setFormError(message);
        setErrorTick((tick) => tick + 1);
    };

    // Wrong credentials: explain, then put the cursor back on the password
    const failAuth = (message) => {
        setFailedAttempts((count) => count + 1);
        showError(message);

        requestAnimationFrame(() => {
            passRef.current?.focus();
            passRef.current?.select();
        });
    };

    const validate = () => {
        const errors = {};
        if (!staffLoginData.user_name?.trim()) {
            errors.user_name = "Enter your staff ID or email.";
        }
        if (!staffLoginData.stud_password) {
            errors.stud_password = "Enter your password.";
        }
        return errors;
    };

    const executeStaffLogin = async () => {
        if (isBusy || !online) return;

        const errors = challenge ? {} : validate();
        if(challenge && !/^\d{6}$/.test(otp)){setFormError("Enter your six-digit authenticator code.");return;}
        setFieldErrors(errors);
        setFormError("");

        if (errors.user_name) return userRef.current?.focus();
        if (errors.stud_password) return passRef.current?.focus();

        setStatus("loading");

        let succeeded = false;

        try {
            const userName = staffLoginData.user_name.trim();
            const BASE_URL = serverHost;

            const loginResponse = await axios.post(challenge?`${BASE_URL}/universe-staff/authentication/verify-otp`:`${BASE_URL}/universe-staff/authentication/login`,challenge?{challengeId:challenge,code:otp}:{user_name:userName,stud_password:staffLoginData.stud_password});

            const loginData = loginResponse.data;

            if(loginData?.status===202){setChallenge(loginData.challengeId);setOtp("");setStaffLoginData({stud_password:""});setStatus("idle");return;}

            if (loginData?.status !== 200) {
                failAuth(
                    loginData?.message ||
                        "We couldn't sign you in. Check your details and try again.",
                );
                return;
            }

            safeSet(REMEMBER_KEY, rememberId ? userName : "");

            dispatch(
                StaffAction.startStaffAction({
                    staffPayload: { staffData: loginData?.staffData },
                    authenticationType: loginData?.authenticationType,
                    sessionToken: loginData?.sessionToken || null,
                }),
            );

            const staffRole = loginData?.staffData?.role?.name
                ?.trim()
                ?.toUpperCase();

            const destination = ROUTES[staffRole];

            if (!destination) {
                showError(
                    "Your staff role doesn't have a workspace yet. Contact administration to get access.",
                );
                return;
            }

            succeeded = true;
            setStatus("success");

            // Short beat so the confirmation is seen before the page changes
            await new Promise((resolve) => setTimeout(resolve, 650));

            redirect(destination);
        } catch (error) {
            console.error("Staff Login Error:", error?.code || "REQUEST_FAILED");

            if (error.code === "ERR_NETWORK") {
                showError(
                    "No internet connection. Check your network and try again.",
                );
            } else if (error.response) {
                failAuth(
                    error.response.data?.message ||
                        "We couldn't sign you in. Check your details and try again.",
                );
            } else {
                showError(
                    error.message || "Something went wrong. Please try again.",
                );
            }
        } finally {
            if (!succeeded) setStatus("idle");
        }
    };

    const role = ROLES[activeRole];

    const submitLabel = {
        idle: "Sign in",
        loading: "Signing in",
        success: "Signed in",
    }[status];

    return (
        <main className="universe-staff-login" data-theme={theme}>
            <section className="usl-shell">
                <button
                    type="button"
                    className="usl-theme-toggle"
                    onClick={toggleTheme}
                    aria-label={
                        theme === "dark"
                            ? "Switch to light mode"
                            : "Switch to dark mode"
                    }
                >
                    {theme === "dark" ? (
                        <LightModeOutlinedIcon />
                    ) : (
                        <DarkModeOutlinedIcon />
                    )}
                </button>

                {/* LEFT: showcase (becomes a header band on tablet/mobile) */}
                <aside className="usl-showcase">
                    <svg
                        className="usl-orbit"
                        viewBox="0 0 600 600"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <circle cx="300" cy="300" r="110" />
                        <circle cx="300" cy="300" r="190" />
                        <circle cx="300" cy="300" r="270" />
                        <g className="usl-orbit-spin usl-orbit-spin--a">
                            <circle
                                className="usl-planet"
                                cx="410"
                                cy="300"
                                r="6"
                            />
                        </g>
                        <g className="usl-orbit-spin usl-orbit-spin--b">
                            <circle
                                className="usl-planet"
                                cx="110"
                                cy="300"
                                r="7"
                            />
                        </g>
                        <g className="usl-orbit-spin usl-orbit-spin--c">
                            <circle
                                className="usl-planet"
                                cx="300"
                                cy="30"
                                r="8"
                            />
                        </g>
                    </svg>

                    <Link to="/" className="usl-brand">
                        <figure>
                            <img src={UniLogo} alt="" />
                        </figure>
                        <div>
                            <strong>Universe Inventory</strong>
                            <span>Management System</span>
                        </div>
                    </Link>

                    <div className="usl-showcase-body">
                        <h1>One sign-in for every Universe role.</h1>

                        <p>
                            Each role opens its own workspace, built around the
                            work that role does.
                        </p>

                        <div
                            className="usl-preview"
                            onMouseEnter={() => setPreviewHovered(true)}
                            onMouseLeave={() => setPreviewHovered(false)}
                        >
                            <div
                                className="usl-tabs"
                                role="tablist"
                                aria-label="Staff roles"
                                onKeyDown={handleTabKeys}
                            >
                                {ROLES.map((item, index) => (
                                    <button
                                        key={item.code}
                                        id={`usl-tab-${item.code}`}
                                        type="button"
                                        role="tab"
                                        className="usl-tab"
                                        aria-selected={index === activeRole}
                                        aria-controls="usl-preview-panel"
                                        tabIndex={index === activeRole ? 0 : -1}
                                        onClick={() => pickRole(index)}
                                    >
                                        {item.code}
                                    </button>
                                ))}
                            </div>

                            <div
                                className="usl-preview-panel"
                                id="usl-preview-panel"
                                role="tabpanel"
                                aria-labelledby={`usl-tab-${role.code}`}
                            >
                                <h3>{role.title}</h3>
                                <p>{role.text}</p>

                                <div className="usl-widgets" aria-hidden="true">
                                    <div className="usl-widget is-accent">
                                        <i />
                                        <b />
                                    </div>
                                    <div className="usl-widget">
                                        <i />
                                        <b />
                                    </div>
                                    <div className="usl-widget">
                                        <i />
                                        <b />
                                    </div>
                                </div>

                                <div className="usl-chart" aria-hidden="true">
                                    {role.bars.map((height, index) => (
                                        <span
                                            key={index}
                                            style={{ "--h": `${height}%` }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <footer className="usl-showcase-footer">
                        <p className="usl-security-note">
                            <ShieldOutlinedIcon />
                            Authorized Universe personnel only. Access follows
                            your assigned staff role.
                        </p>
                        <span>
                            © {new Date().getFullYear()} Universe Inventory
                        </span>
                    </footer>
                </aside>

                {/* RIGHT: sign-in */}
                <section className="usl-auth-panel">
                    <div className="usl-form-wrapper">
                        <header className="usl-form-header">
                            <h2>{greeting}</h2>
                            <p>
                                Sign in with your staff credentials to open your
                                workspace.
                            </p>
                        </header>

                        {!online && (
                            <div
                                className="usl-alert usl-alert--warn"
                                role="status"
                            >
                                <WifiOffRoundedIcon />
                                <p>You're offline. Reconnect to sign in.</p>
                            </div>
                        )}

                        {challenge&&<div className="usl-field"><label>Authenticator verification</label><input aria-label="Authenticator code" inputMode="numeric" maxLength={6} value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,""))} placeholder="Enter your six-digit code"/><button type="button" onClick={()=>{setChallenge(null);setOtp("")}}>Use a different sign-in</button></div>}
                        {formError && (
                            <div
                                className="usl-alert"
                                role="alert"
                                key={errorTick}
                            >
                                <ErrorOutlineRoundedIcon />
                                <div>
                                    <p>{formError}</p>
                                    {failedAttempts >= 3 && (
                                        <Link to="/contact-us">
                                            Reset your password
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}

                        <form
                            className="usl-form"
                            noValidate
                            onSubmit={(event) => {
                                event.preventDefault();
                                executeStaffLogin();
                            }}
                        >
                            <div className="usl-field">
                                <label
                                    className="usl-field-label"
                                    htmlFor="usl-user"
                                >
                                    Staff ID or email
                                </label>

                                <div
                                    className={`usl-input-container${
                                        fieldErrors.user_name
                                            ? " is-invalid"
                                            : ""
                                    }`}
                                >
                                    {isEmail ? (
                                        <AlternateEmailRoundedIcon />
                                    ) : (
                                        <BadgeOutlinedIcon />
                                    )}
                                    <input
                                        id="usl-user"
                                        ref={userRef}
                                        type="text"
                                        name="user_name"
                                        inputMode="email"
                                        autoComplete="username"
                                        autoCapitalize="none"
                                        spellCheck={false}
                                        placeholder="Enter staff ID or email"
                                        value={staffLoginData.user_name}
                                        onChange={handleChange("user_name")}
                                        disabled={isBusy || !!challenge}
                                        aria-invalid={Boolean(
                                            fieldErrors.user_name,
                                        )}
                                        aria-describedby={
                                            fieldErrors.user_name
                                                ? "usl-user-error"
                                                : undefined
                                        }
                                    />
                                </div>

                                {fieldErrors.user_name && (
                                    <p
                                        className="usl-field-error"
                                        id="usl-user-error"
                                    >
                                        {fieldErrors.user_name}
                                    </p>
                                )}
                            </div>

                            <div className="usl-field">
                                <div className="usl-password-heading">
                                    <label
                                        className="usl-field-label"
                                        htmlFor="usl-pass"
                                    >
                                        Password
                                    </label>
                                    <Link to="/contact-us">
                                        Forgot password?
                                    </Link>
                                </div>

                                <div
                                    className={`usl-input-container${
                                        fieldErrors.stud_password
                                            ? " is-invalid"
                                            : ""
                                    }`}
                                >
                                    <LockOutlinedIcon />
                                    <input
                                        id="usl-pass"
                                        ref={passRef}
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="stud_password"
                                        autoComplete="current-password"
                                        placeholder="Enter your password"
                                        value={staffLoginData.stud_password}
                                        onChange={handleChange("stud_password")}
                                        onKeyDown={handlePasswordKey}
                                        onKeyUp={handlePasswordKey}
                                        onBlur={() => setCapsLockOn(false)}
                                        disabled={isBusy || !!challenge}
                                        aria-invalid={Boolean(
                                            fieldErrors.stud_password,
                                        )}
                                        aria-describedby={
                                            fieldErrors.stud_password
                                                ? "usl-pass-error"
                                                : undefined
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="usl-password-toggle"
                                        onClick={() =>
                                            setShowPassword(
                                                (previous) => !previous,
                                            )
                                        }
                                        aria-label={
                                            showPassword
                                                ? "Hide password"
                                                : "Show password"
                                        }
                                        aria-pressed={showPassword}
                                    >
                                        {showPassword ? (
                                            <VisibilityOff />
                                        ) : (
                                            <Visibility />
                                        )}
                                    </button>
                                </div>

                                {fieldErrors.stud_password && (
                                    <p
                                        className="usl-field-error"
                                        id="usl-pass-error"
                                    >
                                        {fieldErrors.stud_password}
                                    </p>
                                )}

                                {capsLockOn && (
                                    <p className="usl-hint" role="status">
                                        <KeyboardCapslockRoundedIcon />
                                        Caps Lock is on
                                    </p>
                                )}
                            </div>

                            <label className="usl-remember">
                                <input
                                    type="checkbox"
                                    checked={rememberId}
                                    onChange={(event) =>
                                        setRememberId(event.target.checked)
                                    }
                                />
                                <span>Remember my staff ID on this device</span>
                            </label>

                            <button
                                type="submit"
                                className={`usl-submit${status === "success" ? " is-success" : ""}`}
                                disabled={isBusy || !online}
                                aria-busy={status === "loading"}
                            >
                                {status === "loading" && (
                                    <span
                                        className="usl-spinner"
                                        aria-hidden="true"
                                    />
                                )}
                                {status === "success" && <CheckRoundedIcon />}
                                <span aria-live="polite">
                                    {online ? submitLabel : "Offline"}
                                </span>
                            </button>
                        </form>

                        <p className="usl-access-info">
                            <Link to="/administration/ceo/login" style={{ color: "inherit", fontWeight: 700 }}>Chief Executive Officer? Sign in to the CEO account</Link>
                        </p>
                        <p className="usl-access-info">
                            <ShieldOutlinedIcon />
                            Your workspace is chosen automatically from your
                            staff role.
                        </p>

                        <footer className="usl-form-footer">
                            <span>Can't sign in?</span>
                            <Link to="/contact-us">Contact administration</Link>
                        </footer>
                    </div>
                </section>
            </section>
        </main>
    );
}
